import React, { useEffect, useState } from 'react';
import {
    Box, Typography, Table, TableHead, TableBody, TableRow,
    TableCell, MenuItem, TextField, CircularProgress, Paper,
    Button
} from '@mui/material';
import { TaxService } from '../../services/tax.service';
import { IEmployeeTaxSummary, ITaxYear } from '../../interfaces/tax.interface';
import { AuthService } from '../../services/auth.service';
import { useToast } from '../../context/ToastContext';
import { useTranslation } from 'react-i18next';

const EmployeeTaxList: React.FC = () => {
    const { t } = useTranslation(['tax', 'common']);
    const { showError } = useToast();
    const institutionId = AuthService.getSelectedInstitution()!;
    const [taxYears, setTaxYears] = useState<ITaxYear[]>([]);
    const [selectedYearId, setSelectedYearId] = useState('');
    const [summaries, setSummaries] = useState<IEmployeeTaxSummary[]>([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        TaxService.getTaxYears(institutionId)
            .then(years => {
                setTaxYears(years);
                const active = years.find(y => y.isActive) ?? years[0];
                if (active) setSelectedYearId(active.id!);
            })
            .catch(() => showError(t('shared.loadTaxYearsFailed')));
    }, [institutionId]);

    useEffect(() => {
        if (!selectedYearId) return;
        setLoading(true);
        TaxService.getEmployeeTaxSummary(selectedYearId, institutionId)
            .then(setSummaries)
            .catch(() => showError(t('employeeTaxList.toast.loadSummaryFailed')))
            .finally(() => setLoading(false));
    }, [selectedYearId, institutionId]);

    const totalYtdTax = summaries.reduce((a, s) => a + s.taxDeductedYtd, 0);
    const totalProjected = summaries.reduce((a, s) => a + s.projectedRemainingTax, 0);

    const downloadCertificate = async (userId: string) => {
        try {
            const blob = await TaxService.downloadCertificatePdf(selectedYearId, userId, institutionId);
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url; a.download = `tax-certificate-${userId}.pdf`; a.click();
            URL.revokeObjectURL(url);
        } catch { showError(t('employeeTaxList.toast.downloadFailed')); }
    };

    return (
        <Box p={3}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h5">{t('employeeTaxList.title')}</Typography>
                <TextField select size="small" label={t('shared.taxYear')} value={selectedYearId}
                    onChange={e => setSelectedYearId(e.target.value)} sx={{ width: 160 }}>
                    {taxYears.map(y => <MenuItem key={y.id} value={y.id}>{y.label}</MenuItem>)}
                </TextField>
            </Box>

            <Box display="flex" gap={2} mb={3}>
                {[
                    { label: t('shared.totalEmployees'), value: summaries.length },
                    { label: t('employeeTaxList.totalTaxDeductedYtd'), value: totalYtdTax.toLocaleString() },
                    { label: t('employeeTaxList.projectedRemainingTax'), value: totalProjected.toLocaleString() },
                ].map(card => (
                    <Paper key={card.label} sx={{ p: 2, flex: 1 }}>
                        <Typography variant="caption" color="text.secondary">{card.label}</Typography>
                        <Typography variant="h6" fontWeight={700}>{card.value}</Typography>
                    </Paper>
                ))}
            </Box>

            {loading ? <CircularProgress /> : (
                <Paper>
                    <Table size="small">
                        <TableHead>
                            <TableRow sx={{ background: '#f1f5f9' }}>
                                <TableCell>{t('common:fields.employee')}</TableCell>
                                <TableCell align="right">{t('shared.taxableIncomeYtd')}</TableCell>
                                <TableCell align="right">{t('employeeTaxList.columns.taxDeductedYtd')}</TableCell>
                                <TableCell align="right">{t('employeeTaxList.columns.thisMonthTax')}</TableCell>
                                <TableCell align="right">{t('employeeTaxList.columns.projectedRemaining')}</TableCell>
                                <TableCell align="center">{t('employeeTaxList.columns.certificate')}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {summaries.map(s => (
                                <TableRow key={s.userId}>
                                    <TableCell>{s.userName}</TableCell>
                                    <TableCell align="right">{s.taxableIncomeYtd.toLocaleString()}</TableCell>
                                    <TableCell align="right">
                                        <Typography color="primary.main" fontWeight={600}>
                                            {s.taxDeductedYtd.toLocaleString()}
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="right">{s.taxThisMonth.toLocaleString()}</TableCell>
                                    <TableCell align="right">
                                        <Typography color="warning.main">{s.projectedRemainingTax.toLocaleString()}</Typography>
                                    </TableCell>
                                    <TableCell align="center">
                                        <Button size="small" onClick={() => downloadCertificate(s.userId)}>
                                            {t('employeeTaxList.pdf')}
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </Paper>
            )}
        </Box>
    );
};

export default EmployeeTaxList;
